import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, Box, Typography,
    Tooltip
} from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import axios from 'axios';
import useApi from '../../hooks/useApi';

// --- Model Interfaces ---
// --- Model Interfaces ---
export interface MonthData {
    baselineMonth: string;
    totalBaselineValue: number | null;
    totalForecastValue: number | null;
    percentageChange: number | null;
    changeDirection: string | null;
}

export interface DisplayTypeData {
    baseLineDisplayType: number;
    months: MonthData[];
}

export interface ProjectData {
    proposalID: number;
    proposalNumber: string;
    proposalName: string;
    endDate:string;
    displayTypes: DisplayTypeData[];
}

export interface ProjectBaselineSummaryResponse {
    startMonth: string;
    endMonth: string;
    projects: ProjectData[];
}


export interface ProjectBaselineSummaryProps {
    baselineId: number;
}

// --- Component ---
const ProjectBaselineSummary: React.FC<ProjectBaselineSummaryProps> = ({ baselineId }) => {
    const { execute } = useApi();
    const [projects, setProjects] = useState<ProjectData[]>([]);
    const [displayType, setDisplayType] = useState<number | null>(null);

    useEffect(() => {
        fetchData();
    }, [baselineId]);

    const fetchData = async () => {
        try {
            const url = `/ProjectBaseline/summary/${baselineId}`;
            const res = await execute<ProjectBaselineSummaryResponse>("get", url); // ✅ typed response

            console.log("response", res.data);

            // safely extract
            setProjects(res.data?.projects ?? []);
        } catch (error) {
            console.error("Error fetching data:", error);
            setProjects([]);
        }
    };
    const handleFilter = (type: number) => {
        setDisplayType(type);
    };

    const filteredProjects = projects.map((proj) => {
        const allMonths = proj.displayTypes.flatMap((d) =>
            d.months.map((m) => ({
                ...m,
                baseLineDisplayType: d.baseLineDisplayType,
            }))
        );

        return {
            ...proj,
            months: displayType
                ? allMonths.filter((m) => m.baseLineDisplayType === displayType)
                : allMonths,
        };
    });
    // --- Collect unique months across all projects ---
    const allMonths = Array.from(
        new Set(
            projects.flatMap((p) =>
                p.displayTypes.flatMap((d) =>
                    d.months.map((m) => m.baselineMonth)
                )
            )
        )
    ).sort();

    return (
        <Box p={3}>
            <Box mb={2} display="flex" gap={2}>
                <Button
                    variant={displayType === 1 ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => handleFilter(1)}
                >
                    Definite
                </Button>
                <Button
                    variant={displayType === 2 ? "contained" : "outlined"}
                    color="secondary"
                    onClick={() => handleFilter(2)}
                >
                    Proposed
                </Button>
                <Button
                    variant={displayType === 5 ? "contained" : "outlined"}
                    color="success"
                    onClick={() => handleFilter(5)}
                >
                    Third-Party
                </Button>
                {/* <Button
                    variant={!displayType ? "contained" : "outlined"}
                    onClick={() => setDisplayType(null)}
                >
                    Show All
                </Button> */}
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    boxShadow: 2,
                    flex: 1,
                    overflowX: "auto",
                     overflowY: "auto",
                    maxHeight: "45vh",
                   
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        
                        //borderCollapse: "none",
                        "& td, & th": {
                            border: "1px solid #ccc",
                        },
                    }}
                >
                    {/* --- Header Row 1 --- */}
                    <TableHead>
                        {/* --- Header Row 1 --- */}
                        <TableRow
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 3,
                                backgroundColor: "#92d050",
                                "& th": {
                                    backgroundColor: "#1e66ab",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                    padding: "6px 8px", // reduce padding to keep compact
                                },
                            }}
                        >
                            <TableCell rowSpan={2} align="center"
                                sx={{ position: "sticky",
                                left: 0, zIndex:6, backgroundColor: "#92d050 !important",width: 300, minWidth: 300, }}>
                                Proposal Number
                            </TableCell>
                            <TableCell rowSpan={2} align="center"
                                sx={{ position:"sticky",left:300, zIndex:6,width: 300, minWidth: 300, backgroundColor: "#92d050 !important", }}>
                                Description
                            </TableCell>
                            {allMonths.map((month) => (
                                <TableCell key={month} align="center" colSpan={2}>
                                    {new Date(month).toLocaleString("default", {
                                        month: "short",
                                        year: "2-digit",
                                    })}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* --- Header Row 2 --- */}
                        <TableRow
                            sx={{
                                position: "sticky",
                                top: 20,           // 👈 height of header row 1
                                //top: "calc(56px)",
                                zIndex: 2,
                                backgroundColor: "#92d050",
                                "& th": {
                                    backgroundColor: "#92d050",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                    padding: "6px 8px", // keep consistent compact height
                                },
                            }}
                        >
                            {allMonths.map((month) => (
                                <React.Fragment key={`${month}-sub`}>
                                    <TableCell align="center" 
                                    sx={{   
                                             position: "sticky",
                                                top: 38, 
                                                
                                             width:150,
                                             zIndex: 3,
                                            borderLeft: "2px solid #90caf9",
                                        }}>Baseline</TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            position: "sticky",
                                                top: 38,
                                        
                                            width:150,
                                             zIndex: 3,
                                            borderLeft: "2px solid #90caf9",
                                        }}
                                    >
                                        Change%
                                    </TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* --- Table Body --- */}
                    <TableBody>
                        {filteredProjects.map((proj, idx) => (
                            <TableRow
                                key={proj.proposalID}
                                sx={{
                                   
                                    backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f5f5f5",
                                }}
                            >
                                <TableCell sx={{
                                    position: "sticky",
                                    left: 0,
                                    
                                    backgroundColor: "#fff",
                                    width: 300, minWidth: 300,
                                }}
                                >{proj.proposalNumber}</TableCell>
                                <TableCell 
                                sx={{
                                position: "sticky",
                                left: 300,
                                backgroundColor: "#fff",
                                width: 300, minWidth: 300,
                                }}>{proj.proposalName}</TableCell>
                                {allMonths.map((month) => {
                                    const data = proj.months.find((m) => m.baselineMonth === month);

                                    const monthDate = new Date(month);
                                    const proposalEnd = new Date(proj.endDate);

                                    const isGray = monthDate >= proposalEnd;

                                    return (
                                        <React.Fragment key={`${proj.proposalID}-${month}`}>
                                            <TableCell align="center"
                                                sx={{
                                                    backgroundColor: isGray ? "#e0e0e0" : "inherit",
                                                    color: isGray ? "#888" : "inherit",
                                                }}>
                                                {data
                                                    ? data.totalBaselineValue?.toLocaleString()
                                                    : ""} 
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    borderLeft: "2px solid #90caf9",
                                                    backgroundColor: isGray ? "#e0e0e0" : "inherit",
                                                    color: isGray ? "#888" : "inherit",
                                                }}
                                            >
                                                {data && data.percentageChange !== null ? (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        gap={0.5}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                <Box>
                                                                    <div><strong>Baseline:</strong> {data.totalBaselineValue ?? "N/A"}</div>
                                                                    <div><strong>Forecast:</strong> {data.totalForecastValue ?? "N/A"}</div>
                                                                </Box>
                                                            }
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <Typography
                                                                color={
                                                                    data.changeDirection === "Up"
                                                                        ? "green"
                                                                        : data.changeDirection === "Down"
                                                                            ? "red"
                                                                            : "inherit"
                                                                }
                                                                fontWeight="bold"
                                                                sx={{ cursor: "pointer" }}
                                                            >
                                                                {data.percentageChange}%
                                                            </Typography>
                                                        </Tooltip>
                                                        {data.changeDirection === "Up" && (
                                                            <ArrowDropUp sx={{ color: "green" }} />
                                                        )}
                                                        {data.changeDirection === "Down" && (
                                                            <ArrowDropDown sx={{ color: "red" }} />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ProjectBaselineSummary;